import { RSAA } from 'redux-api-middleware';

import { actions as spidActions } from '../schibstedAccount';

import { getJWT, getPulseJWE, resetAuthStateRedirectLocation } from './utils';
import { getFullUrlTo } from '../../util/uri';

import { CONTENT_TYPE_HEADER, CONTENT_TYPES } from '../../constants/http';

import {
    VERIFY_JWT_REQUEST,
    VERIFY_JWT_SUCCESS,
    VERIFY_JWT_FAILURE,
    LOGIN_REQUEST,
    LOGIN_SUCCESS,
    LOGIN_FAILURE,
    LOGOUT_REQUEST,
    LOGOUT_SUCCESS,
    LOGOUT_FAILURE,
    EXCHANGE_PULSE_TOKEN_REQUEST,
    EXCHANGE_PULSE_TOKEN_SUCCESS,
    EXCHANGE_PULSE_TOKEN_FAILURE,
    LOGIN_USER_USING_SCHIBSTED_ACCOUNT,
    LOGIN_USER_USING_PULSE,
    FORCE_NEW_SESSION,
    MY_PAGE_AUTH_COMPLETE,
    SCHIBSTED_ACCOUNT_LOGIN,
    LOGIN_DIFFERENT_ACCOUNT,
} from './types';

export const initializeSchibstedAccount = ({ redirectUri, logoutRedirectUri } = {}) => spidActions.start({
    ...CONFIG.spid,
    redirectUri: redirectUri || getFullUrlTo('/login'),
    logoutRedirectUri,
});

export const verifyJWT = ({ endpoint }) => ({
    [RSAA]: {
        endpoint: `${endpoint}`,
        method: 'GET',
        headers: {
            Authorization: getJWT(),
            Publisher: CONFIG.myPage.publication,
        },
        types: [
            VERIFY_JWT_REQUEST,
            VERIFY_JWT_SUCCESS,
            VERIFY_JWT_FAILURE,
        ],
    },
});

export const loginUser = ({ endpoint }) => ({
    [RSAA]: {
        endpoint: `${endpoint}`,
        method: 'GET',
        headers: {
            Publisher: CONFIG.myPage.publication,
            'X-Frontend-Version': process.env.FRONTEND_VERSION_HASH || '',
            'X-Frontend-Build-Time': process.env.FRONTEND_BUILD_TIMESTAMP || '',
        },
        types: [
            LOGIN_REQUEST,
            LOGIN_SUCCESS,
            LOGIN_FAILURE,
        ],
    },
});

export const logoutUser = ({ endpoint }) => ({
    [RSAA]: {
        endpoint: `${endpoint}`,
        method: 'GET',
        credentials: 'include',
        headers: {
            Authorization: getJWT(),
            Publisher: CONFIG.myPage.publication,
            'X-Frontend-Version': process.env.FRONTEND_VERSION_HASH || '',
            'X-Frontend-Build-Time': process.env.FRONTEND_BUILD_TIMESTAMP || '',
        },
        types: [
            LOGOUT_REQUEST,
            LOGOUT_SUCCESS,
            LOGOUT_FAILURE,
        ],
    },
});

export const exchangePulseToken = ({ endpoint }) => ({
    [RSAA]: {
        endpoint: `${endpoint}`,
        method: 'POST',
        headers: {
            Authorization: `Bearer ${getPulseJWE()}`,
            [CONTENT_TYPE_HEADER]: CONTENT_TYPES.JSON,
            Publisher: CONFIG.myPage.publication,
            'X-Frontend-Version': process.env.FRONTEND_VERSION_HASH || '',
            'X-Frontend-Build-Time': process.env.FRONTEND_BUILD_TIMESTAMP || '',
        },
        types: [
            EXCHANGE_PULSE_TOKEN_REQUEST,
            EXCHANGE_PULSE_TOKEN_SUCCESS,
            EXCHANGE_PULSE_TOKEN_FAILURE,
        ],
    },
});

export const loginUserUsingSchibstedAccount = payload => ({
    type: LOGIN_USER_USING_SCHIBSTED_ACCOUNT,
    payload,
});

export const loginUserUsingPulse = () => ({
    type: LOGIN_USER_USING_PULSE,
});

export const forceNewSession = () => ({
    type: FORCE_NEW_SESSION,
});

export const authenticationProcessComplete = () => ({
    type: MY_PAGE_AUTH_COMPLETE,
});

/**
 * Creates an action for Schibsted Account login using identity information
 * @param {Object} payload - The login payload
 * @param {string} payload.uuid - Unique identifier for the user account that serves as login hint
 * @param {Object} payload.redirectLocation - Redirect location object describing where to redirect after login
 * @returns {Object} Redux action with type and payload containing loginHint and state
 */
export const loginUsingIdentity = payload => ({
    type: SCHIBSTED_ACCOUNT_LOGIN,
    payload: {
        loginHint: payload?.uuid || '',
        state: resetAuthStateRedirectLocation(payload?.redirectLocation || undefined),
        scope: 'openid offline_access',
    },
});

export const loginToDifferentAccount = payload => ({
    type: LOGIN_DIFFERENT_ACCOUNT,
    payload,
});
